import { ref, computed, readonly } from 'vue';
import { authService } from '@/services/api/authService';

// État global singleton
const user = ref(null);
const loading = ref(false);
const error = ref(null);
const initialized = ref(false);

export function useAuth() {
  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() => user.value?.role === 'ADMIN');

  /**
   * Initialiser l'état d'authentification au démarrage
   */
  async function initialize() {
    if (initialized.value) return;

    loading.value = true;
    error.value = null;

    try {
      const storedUser = authService.getStoredUser();
      if (storedUser && authService.isAuthenticated()) {
        // Vérifier que le token est toujours valide
        user.value = await authService.checkSession();
      }
    } catch (err) {
      console.error('Auth initialization error:', err);
      user.value = null;
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  /**
   * Inscription
   */
  async function register(email, password, displayName = null) {
    loading.value = true;
    error.value = null;

    try {
      user.value = await authService.register(email, password, displayName);
      return user.value;
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors de l\'inscription';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Connexion
   */
  async function login(email, password) {
    loading.value = true;
    error.value = null;

    try {
      user.value = await authService.login(email, password);
      return user.value;
    } catch (err) {
      error.value = err.response?.data?.message || 'Email ou mot de passe incorrect';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Déconnexion
   */
  function logout() {
    authService.logout();
    user.value = null;
  }

  /**
   * Mettre à jour le profil
   */
  async function updateProfile(displayName, preferences) {
    loading.value = true;
    error.value = null;

    try {
      user.value = await authService.updateProfile(displayName, preferences);
      return user.value;
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors de la mise à jour';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Changer le mot de passe
   */
  async function changePassword(currentPassword, newPassword) {
    loading.value = true;
    error.value = null;

    try {
      await authService.changePassword(currentPassword, newPassword);
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors du changement de mot de passe';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Effacer l'erreur
   */
  function clearError() {
    error.value = null;
  }

  return {
    // État (readonly pour éviter les modifications directes)
    user: readonly(user),
    loading: readonly(loading),
    error: readonly(error),
    isAuthenticated,
    isAdmin,
    initialized: readonly(initialized),

    // Actions
    initialize,
    register,
    login,
    logout,
    updateProfile,
    changePassword,
    clearError
  };
}
